import React from 'react';
import { fetchCartItemsFromServer, saveCartItemsToServer } from '../api/woocommerce/cart-items.api';
import { setCartItems, type CartLineItem } from '../store/cart/cart.slice';
import { useAppDispatch, useAppSelector } from '../store/hooks';

const normalizeCartItem = (item: unknown): CartLineItem | null => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const candidate = item as Partial<CartLineItem>;
  const id = Number(candidate.id);
  const quantity = Math.max(1, Number(candidate.quantity) || 1);
  const price = Number(candidate.price) || 0;

  if (!Number.isFinite(id) || !candidate.name) {
    return null;
  }

  return {
    id,
    name: String(candidate.name),
    price,
    image: typeof candidate.image === 'string' ? candidate.image : '',
    quantity,
    selectedAttributes:
      candidate.selectedAttributes && typeof candidate.selectedAttributes === 'object'
        ? Object.entries(candidate.selectedAttributes).reduce<Record<string, string>>((accumulator, [key, value]) => {
            if (typeof value === 'string') {
              accumulator[key] = value;
            }
            return accumulator;
          }, {})
        : undefined,
  };
};

const normalizeCartItems = (items: unknown): CartLineItem[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => normalizeCartItem(item))
    .filter((item): item is CartLineItem => item !== null);
};

const getCartSignature = (items: CartLineItem[]): string =>
  JSON.stringify(
    items
      .map((item) => ({
        id: item.id,
        quantity: item.quantity,
        name: item.name,
        price: item.price,
        image: item.image,
        selectedAttributes: item.selectedAttributes ?? {},
      }))
      .sort((first, second) => first.id - second.id)
  );

const CartSyncBridge: React.FC = () => {
  const dispatch = useAppDispatch();
  const cartItems = useAppSelector((state) => state.cart.items);
  const { isAuthenticated, token } = useAppSelector((state) => state.auth);

  const hydratedTokenRef = React.useRef<string | null>(null);
  const isHydratingRef = React.useRef(false);
  const lastSyncedSignatureRef = React.useRef<string>('');

  React.useEffect(() => {
    if (!isAuthenticated || !token) {
      hydratedTokenRef.current = null;
      isHydratingRef.current = false;
      lastSyncedSignatureRef.current = '';
      return;
    }

    if (hydratedTokenRef.current === token) {
      return;
    }

    hydratedTokenRef.current = token;
    let isActive = true;

    const hydrateCart = async (): Promise<void> => {
      const localItems = cartItems;

      try {
        isHydratingRef.current = true;
        const serverItems = normalizeCartItems(await fetchCartItemsFromServer(token));

        if (serverItems.length > 0) {
          if (isActive) {
            dispatch(setCartItems(serverItems));
            lastSyncedSignatureRef.current = getCartSignature(serverItems);
          }
          return;
        }

        if (localItems.length > 0) {
          await saveCartItemsToServer(localItems, token);
          lastSyncedSignatureRef.current = getCartSignature(localItems);
        }
      } catch (error) {
        console.error('Failed to hydrate cart items from server:', error);
      } finally {
        if (isActive) {
          isHydratingRef.current = false;
        }
      }
    };

    void hydrateCart();

    return () => {
      isActive = false;
      isHydratingRef.current = false;
    };
  }, [cartItems, dispatch, isAuthenticated, token]);

  React.useEffect(() => {
    if (!isAuthenticated || !token || isHydratingRef.current) {
      return;
    }

    const signature = getCartSignature(cartItems);

    if (signature === lastSyncedSignatureRef.current) {
      return;
    }

    lastSyncedSignatureRef.current = signature;

    const syncCart = async (): Promise<void> => {
      try {
        await saveCartItemsToServer(cartItems, token);
      } catch (error) {
        console.error('Failed to sync cart items to server:', error);
      }
    };

    void syncCart();
  }, [cartItems, isAuthenticated, token]);

  return null;
};

export default CartSyncBridge;
